import { CurrencyFormatters } from "halifax";
import { FiatPrice, RewardsPrice } from "redmond";

import { ParentState } from "@capone/checkout";
import type * as H from "history";
import queryStringParser from "query-string";

import type { StateValue, send as xstateSend } from "xstate";
import { Event } from "../../../modules/book/state/events";

export const validateContext = () => true;
// Boolean(
//   (ctx.flightShop.shopPricingInfo.fare?.length || -1) > 0 &&
//     ctx.flightSearch.departureDate &&
//     ctx.flightSearch.destination &&
//     ctx.flightSearch.origin &&
//     ctx.flightShop.selectedTrip.outgoingFareId &&
//     ctx.flightShop.selectedTrip.outgoingSliceId &&
//     ctx.flightShop.selectedTrip.tripId &&
//     Object.keys(ctx.flightShop.airports || {}).length > 0
// );

export const formatCurrency = (price: FiatPrice, discount?: boolean) =>
  CurrencyFormatters.get(price.currencyCode).format(
    discount ? Math.abs(price.value) * -1 : price.value
  );

export const formatRewards = (price: RewardsPrice, discount?: boolean) => {
  const priceValueToUse = discount ? Math.abs(price.value) : price.value;

  const isCash = price.currencyDescription?.includes("cash");

  const currencySymbol = isCash ? "$" : "";
  return `${
    discount ? "-" : ""
  }${currencySymbol}${priceValueToUse.toLocaleString("en-US", {
    minimumFractionDigits: isCash ? 2 : 0,
    maximumFractionDigits: isCash ? 2 : 0,
  })} ${price?.currencyDescription}`;
};

/**
 *  Stringifies first StateValue to add to url params
 * @example
 * stringifyParentStateValue({ passengerInfo: { picker: "idle" } });
 * returns "passengerInfo"
 */
export const stringifyParentStateValue = (state: StateValue) => {
  if (typeof state === "string") return state;
  if (typeof state === "object") {
    const key = Object.keys(state)[0];
    return key;
  }
  return "";
};

const untrackedStates = [
  ParentState.loading,
  ParentState.cartQuote,
  ParentState.cartUpdate,
  ParentState.cartFulfill,
];

// Map of states that should not transition when query params change
// Map: { state that should not transition => previous tracked state }
// Eg: if user is in "cartQuote" and hits back on their browser, the user should not be allowed to go back 1 step and
// instead stay in the current step till it resolves
const preventTransitionStates = {
  [ParentState.cartQuote]: ParentState.activityTravelerInformation,
  [ParentState.cartUpdate]: ParentState.activityTravelerInformation,
  [ParentState.cartFulfill]: ParentState.cardPayment,
};

enum MobileSteps {
  activityTravelerInformation,
  contactInformation,
  activityPrimaryTraveler,
  activityAdditionalDetails,
  cardPayment,
  review,
  bookingConfirmation,
}
export const mapStepToNumberString = {
  activityTravelerInformation: "1",
  contactInformation: "2",
  activityPrimaryTraveler: "3",
  activityAdditionalDetails: "4",
  cardPayment: "5",
  review: "6",
  bookingConfirmation: "7",
};

export const populateCheckoutQueryParams = (
  state: StateValue,
  history: H.History
) => {
  const queryString = history?.location?.search || "";
  const parsedQuery = queryStringParser.parse(queryString);
  const stateQueryParam = parsedQuery?.bookStep;

  const stringifiedNewState = stringifyParentStateValue(state);

  const shouldUpdateQuery = !untrackedStates.includes(
    stringifiedNewState as ParentState
  );

  const bookStepInString = MobileSteps[Number(stateQueryParam) - 1];

  //  If xstate state value is not in query params or if it conflicts with what the
  //  current query params contains, then we push new query params to the URL

  // const bookStepInNumber =
  //   stringifiedNewState in mapStepToNumberString
  //     ?  MobileSteps[stringifiedNewState]
  //     : undefined;

  const historyData = {
    pathname: history.location.pathname,
    search: queryStringParser.stringify({
      ...parsedQuery,
      bookStep: mapStepToNumberString[stringifiedNewState],
    }),
    state: history.location.state,
  };

  if (!stateQueryParam && shouldUpdateQuery) {
    history.replace(historyData);
  } else if (
    stateQueryParam &&
    bookStepInString &&
    stringifiedNewState !== bookStepInString &&
    shouldUpdateQuery
  ) {
    history.push(historyData);
  }
};

export const mapFlightStateToTransitionEvent = {
  [ParentState.activityTravelerInformation]: Event.GO_TO_TRAVELER_SELECT,
  [ParentState.contactInformation]: Event.GO_TO_CONTACT_INFORMATION,
  [ParentState.activityPrimaryTraveler]: Event.GO_TO_PRIMARY_TRAVELER_SELECT,
  [ParentState.activityAdditionalDetails]: Event.GO_TO_ADDITIONAL_DETAILS,
  [ParentState.cardPayment]: Event.GO_TO_CARD_PAYMENT,
  [ParentState.review]: Event.GO_TO_REVIEW,
  [ParentState.bookingConfirmation]: Event.GO_TO_REVIEW,
};

export const transitionStateOnPathnameChange = (
  state: StateValue,
  history: H.History,
  send: typeof xstateSend
) => {
  const queryString = history.location.search;
  const parsedQuery = queryStringParser.parse(queryString);
  const stateQueryParam = parsedQuery?.bookStep;

  const stringifiedNewState = stringifyParentStateValue(state);

  const preventTransition = !!preventTransitionStates[stringifiedNewState];

  // `bookstep` is a number to match the other funnels not on checkout lib
  const bookStepInString = MobileSteps[Number(stateQueryParam) - 1];

  const allowTransition =
    !preventTransition &&
    stateQueryParam &&
    bookStepInString &&
    !untrackedStates.includes(bookStepInString as ParentState);

  const hasSucceededFulfillment =
    stringifiedNewState === ParentState.bookingConfirmation;

  if (
    stateQueryParam &&
    bookStepInString &&
    stringifiedNewState !== bookStepInString &&
    allowTransition
  ) {
    if (hasSucceededFulfillment) {
      history.push("/");
    } else {
      send({
        type: mapFlightStateToTransitionEvent[bookStepInString as ParentState],
      });
    }
  } else if (
    preventTransition &&
    bookStepInString &&
    preventTransitionStates[stringifiedNewState] !== bookStepInString
  ) {
    const historyData = {
      pathname: history.location.pathname,
      search: queryStringParser.stringify({
        ...parsedQuery,
        bookStep:
          mapStepToNumberString[preventTransitionStates[stringifiedNewState]],
      }),
      state: history.location.state,
    };
    // Pushing the same stack that was removed when the user goes 1 step back in their browser
    history.push(historyData);
  }
};
